import { DateTime } from "luxon";
import { estAdministrateurOperateur, estUtilisateurOperateur } from "../../../helpers/rolesManager";

// ============================================================
// Comptes d'un opérateur d'accompagnement (CE-1407) — fonctions pures,
// testables sans DOM : états, libellés, filtres locaux de l'onglet
// Utilisateurs, formulaire de la modale et droits de celui qui édite.
// L'« éditeur » est l'utilisateur connecté (jeton de session) : un agent de
// la Caf peut tout ; un administrateur d'opérateur ne touche qu'aux champs
// que l'API lui laisse, et jamais à son propre compte pour le désactiver.
// ============================================================

// Textes repris de la maquette 10 validée par la Caf.
export const ROLES_COMPTE_OPERATEUR = [
  {
    id: "ADMIN_OPERATEUR",
    name: "Administrateur",
    description: "Gère les comptes des manageurs et des agents de la structure, ses calendriers et ses coordonnées.",
  },
  {
    id: "MANAGEUR_OPERATEUR",
    name: "Manageur",
    description: "Répartit les dossiers reçus entre les agents et suit l'activité de la structure.",
  },
  {
    id: "AGENT_OPERATEUR",
    name: "Agent",
    description: "Accompagne les allocataires qui lui sont attribués.",
  },
];

export const libelleRoleCompte = (role) => {
  const trouve = ROLES_COMPTE_OPERATEUR.find((r) => r.id === role);
  return trouve ? trouve.name : role || "";
};

// Un compte est activé, désactivé (a été activé puis coupé) ou en attente
// d'activation (jamais activé) : mêmes règles que la fiche utilisateur Caf.
export function etatCompteOperateur(compte) {
  if (!compte) return null;
  if (compte.estActive) {
    return { id: "active", libelle: "Compte activé", court: "Activé", ton: "ok", icone: "fa-check" };
  }
  if (compte.activeLe) {
    return { id: "desactive", libelle: "Compte désactivé", court: "Désactivé", ton: "ko", icone: "fa-power-off" };
  }
  return { id: "attente", libelle: "Activation en attente", court: "Invitation envoyée", ton: "warn", icone: "fa-hourglass-half" };
}

export const estDesactive = (compte) => etatCompteOperateur(compte).id === "desactive";
export const estEnAttente = (compte) => etatCompteOperateur(compte).id === "attente";

// « Prénom NOM » ; l'e-mail quand le nom manque.
export function nomCompletCompte(compte) {
  if (!compte) return "";
  const prenom = String(compte.prenom || "").trim();
  const nom = String(compte.nom || "")
    .trim()
    .toUpperCase();
  return [prenom, nom].filter(Boolean).join(" ") || compte.email || "";
}

const pluriel = (n, singulier, plurielMot = `${singulier}s`) => `${n} ${n > 1 ? plurielMot : singulier}`;

// « 3 comptes, dont 1 invitation en attente d'activation » : les comptes
// désactivés ne comptent pas.
export function resumeComptes(comptes = []) {
  const vivants = (comptes || []).filter((c) => !estDesactive(c));
  if (!vivants.length) return "";
  const attente = vivants.filter(estEnAttente).length;
  const base = pluriel(vivants.length, "compte");
  return attente ? `${base}, dont ${pluriel(attente, "invitation")} en attente d'activation` : base;
}

export const nombreAdministrateursActives = (comptes) =>
  (Array.isArray(comptes) ? comptes : []).filter((c) => estAdministrateurOperateur(c.role) && c.estActive).length;

export function normaliser(texte) {
  return String(texte || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

export function filtrerComptes(comptes = [], { terme = "", role = "", voirDesactives = false } = {}) {
  const t = normaliser(terme);
  return (comptes || []).filter(
    (c) =>
      (voirDesactives || !estDesactive(c)) &&
      (!role || c.role === role) &&
      (!t || normaliser(`${c.prenom} ${c.nom} ${c.email}`).includes(t))
  );
}

export function compterParRole(comptes = [], voirDesactives = false) {
  const base = (comptes || []).filter((c) => voirDesactives || !estDesactive(c));
  const compte = { tous: base.length };
  ROLES_COMPTE_OPERATEUR.forEach((r) => {
    compte[r.id] = base.filter((c) => c.role === r.id).length;
  });
  return compte;
}

// --- Formulaire de la modale ---------------------------------

// Pas de mot de passe : le titulaire le choisit en activant son compte
// (évolution du 20/10/2026).
export const FORMULAIRE_COMPTE_VIDE = {
  civilite: 1,
  prenom: "",
  nom: "",
  email: "",
  telephone: "",
  role: "AGENT_OPERATEUR",
  sites: [],
  dateFinContrat: "",
  recoitAlertesPlanning: false,
};

// Valeur d'un <input type="date"> à partir d'une date ISO (vide sinon).
export function versDateSaisie(valeur) {
  if (!valeur) return "";
  const dt = DateTime.fromISO(String(valeur));
  return dt.isValid ? dt.toFormat("yyyy-MM-dd") : "";
}

export function formulaireDepuisCompte(compte = {}) {
  return {
    civilite: compte.civilite == null ? 1 : compte.civilite,
    prenom: compte.prenom || "",
    nom: compte.nom || "",
    email: compte.email || "",
    telephone: compte.telephone || "",
    role: compte.role || "AGENT_OPERATEUR",
    sites: Array.isArray(compte.sites) ? [...compte.sites] : [],
    dateFinContrat: versDateSaisie(compte.dateFinContrat),
    recoitAlertesPlanning: Boolean(compte.recoitAlertesPlanning),
  };
}

const editeurEstCaf = (editeur) => !estUtilisateurOperateur(editeur);

// Rôles proposés dans la modale : seule la Caf nomme un administrateur.
export function rolesProposes({ editeur } = {}) {
  if (editeurEstCaf(editeur)) return ROLES_COMPTE_OPERATEUR.map((r) => ({ ...r }));
  return ROLES_COMPTE_OPERATEUR.map((r) =>
    estAdministrateurOperateur(r.id) ? { ...r, disabled: true, aide: "Attribué par la Caf." } : { ...r }
  );
}

// Champs affichés en lecture seule pour l'éditeur : l'administrateur
// d'opérateur ne fixe ni l'e-mail ni la date de fin de contrat, et ne change
// pas le rôle d'un administrateur.
export function champsVerrouilles({ editeur, compte } = {}) {
  if (editeurEstCaf(editeur)) return [];
  const verrous = ["email", "dateFinContrat"];
  if (compte && estAdministrateurOperateur(compte.role)) verrous.push("role");
  return verrous;
}

const texteOuNull = (valeur) => {
  const t = String(valeur || "").trim();
  return t ? t : null;
};

export function corpsCreation(formulaire) {
  const corps = {
    civilite: Number(formulaire.civilite) || 1,
    prenom: String(formulaire.prenom || "").trim(),
    nom: String(formulaire.nom || "").trim(),
    email: String(formulaire.email || "")
      .trim()
      .toLowerCase(),
    role: formulaire.role,
    sites: [...(formulaire.sites || [])],
    recoitAlertesPlanning: Boolean(formulaire.recoitAlertesPlanning),
  };
  const telephone = texteOuNull(formulaire.telephone);
  if (telephone) corps.telephone = telephone;
  const dateFinContrat = texteOuNull(formulaire.dateFinContrat);
  if (dateFinContrat) corps.dateFinContrat = dateFinContrat;
  return corps;
}

const CLES_ADMINISTRATEUR_OPERATEUR = ["civilite", "prenom", "nom", "telephone", "recoitAlertesPlanning", "role", "sites"];

export function corpsModification(formulaire, { editeur, compte } = {}) {
  const complet = {
    civilite: Number(formulaire.civilite) || 1,
    prenom: String(formulaire.prenom || "").trim(),
    nom: String(formulaire.nom || "").trim(),
    email: String(formulaire.email || "")
      .trim()
      .toLowerCase(),
    telephone: texteOuNull(formulaire.telephone),
    role: formulaire.role,
    sites: [...(formulaire.sites || [])],
    dateFinContrat: texteOuNull(formulaire.dateFinContrat),
    recoitAlertesPlanning: Boolean(formulaire.recoitAlertesPlanning),
  };
  if (editeurEstCaf(editeur)) return complet;
  const verrous = champsVerrouilles({ editeur, compte });
  return CLES_ADMINISTRATEUR_OPERATEUR.filter((cle) => !verrous.includes(cle)).reduce((corps, cle) => {
    corps[cle] = complet[cle];
    return corps;
  }, {});
}

// Un administrateur d'opérateur ne se désactive pas lui-même.
export function peutDesactiver({ editeur, compte } = {}) {
  if (editeurEstCaf(editeur)) return true;
  return Boolean(editeur && compte) && String(editeur.id) !== String(compte.id);
}

// --- Sites d'un compte (évolution du 20/10/2026) --------------

// Nom court d'un site dans sa structure : « AREP Saint-Leu » → « Saint-Leu ».
export function nomCourtSite(site, nomStructure = "") {
  const nom = String((site && site.nom) || "").trim();
  const prefixe = String(nomStructure || "").trim();
  if (prefixe && nom.toLowerCase().startsWith(prefixe.toLowerCase() + " ")) {
    return nom.slice(prefixe.length + 1).trim() || nom;
  }
  return nom;
}

// « SAINT-ANDRÉ » → « Saint-André » : les communes du référentiel sont en
// capitales, la maquette les écrit en casse normale.
export function communeLisible(ville) {
  return String(ville || "")
    .trim()
    .toLowerCase()
    .replace(/(^|[\s'-])(\p{L})/gu, (_, separateur, lettre) => separateur + lettre.toUpperCase())
    // Les articles et prépositions entre deux tirets restent en minuscules : La Plaine-des-Palmistes.
    .replace(/-(Des|De|Du|La|Le|Les|Sur|Sous)(?=-)/g, (_, mot) => "-" + mot.toLowerCase());
}

// « 1 autre site », « 2 autres sites ».
export const autresSites = (nombre) => `${nombre} autre${nombre > 1 ? "s" : ""} site${nombre > 1 ? "s" : ""}`;

// Colonne Sites de l'onglet : « Tous les sites », les noms courts dans
// l'ordre de la structure, ou « Aucun site ». Sans la liste des sites
// (pas encore chargée), le nombre. Un compte d'opérateur (`vueLimitee`) ne
// reçoit que ses sites (règle du 20/10/2026) : ceux du compte qu'il ne voit
// pas sont comptés (« + 1 autre site »), et « Tous les sites » ne se dit pas,
// faute de connaître tous ceux de la structure.
export function libelleSitesCompte(compte, sitesStructure = [], nomStructure = "", { vueLimitee = false } = {}) {
  const ids = (compte && compte.sites) || [];
  if (!sitesStructure.length) {
    return ids.length ? `${ids.length} site${ids.length > 1 ? "s" : ""}` : "Aucun site";
  }
  const retenus = sitesStructure.filter((site) => ids.includes(site.id));
  const autres = vueLimitee ? ids.filter((id) => !sitesStructure.some((site) => site.id === id)).length : 0;
  if (!retenus.length && !autres) return "Aucun site";
  if (!vueLimitee && retenus.length === sitesStructure.length) return "Tous les sites";
  const noms = retenus.map((site) => nomCourtSite(site, nomStructure)).join(", ");
  if (!autres) return noms;
  return noms ? `${noms} + ${autresSites(autres)}` : autresSites(autres);
}

// --- Encadré « Ce que ce profil peut faire » ------------------

// Textes de la maquette 10 : les droits que donnent les régimes de la
// structure, puis ce qu'ajoute le rôle. Ils décrivent la cible : les droits
// sur les dossiers arrivent avec la sous-orientation (CE-1370).
const DROITS_REGIMES = {
  TOTAL: {
    titre: "En accompagnement total",
    texte: "Référent unique des allocataires attribués : rendez-vous, contrat, contradictoire, inscription en EP, réorientation.",
  },
  PARTIEL: {
    titre: "En accompagnement partiel",
    texte: "Voit l'identité, la fiche, le RDO et l'existence d'un contrat ; pointe les rendez-vous (honoré, non honoré).",
  },
};

// Règle du 20/10/2026 : quel que soit le rôle, un compte ne voit que ses sites.
const DROITS_ROLES = {
  ADMIN_OPERATEUR: {
    titre: "En plus, comme administrateur",
    texte: "Voit tous les dossiers de ses sites, les répartit entre les agents, gère les comptes et les calendriers de ses sites.",
  },
  MANAGEUR_OPERATEUR: {
    titre: "En plus, comme manageur",
    texte: "Voit tous les dossiers de ses sites et les répartit entre les agents.",
  },
};

const DROIT_PERIMETRE = {
  titre: "Sur ses sites seulement",
  texte: "Tout cela ne vaut que sur les sites du compte : il ne voit ni les autres sites de la structure, ni leurs calendriers, comptes ou dossiers.",
};

export function droitsDuProfil({ regimes = [], role } = {}) {
  const droits = ["TOTAL", "PARTIEL"].filter((r) => (regimes || []).includes(r)).map((r) => ({ ...DROITS_REGIMES[r] }));
  if (!droits.length) return droits;
  if (DROITS_ROLES[role]) droits.push({ ...DROITS_ROLES[role] });
  droits.push({ ...DROIT_PERIMETRE });
  return droits;
}

// --- Validation et suivi des modifications --------------------

const ADRESSE_VALIDE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function validerFormulaireCompte(formulaire, { nombreSites = 0, verrous = [] } = {}) {
  const erreurs = {};
  if (!String(formulaire.prenom || "").trim()) erreurs.prenom = "Saisissez le prénom.";
  if (!String(formulaire.nom || "").trim()) erreurs.nom = "Saisissez le nom.";
  if (!verrous.includes("email")) {
    const email = String(formulaire.email || "").trim();
    if (!email) erreurs.email = "Saisissez l'adresse e-mail.";
    else if (!ADRESSE_VALIDE.test(email)) erreurs.email = "Cette adresse n'est pas valide (exemple : prenom.nom@structure.re).";
  }
  // Un compte d'opérateur a toujours au moins un site (règle du 20/10/2026).
  if (!(formulaire.sites || []).length) {
    erreurs.sites =
      nombreSites > 0
        ? "Cochez au moins un site."
        : "Ajoutez d'abord un site à la structure : un compte est toujours rattaché à au moins un site.";
  }
  return erreurs;
}

const serialiser = (formulaire) =>
  JSON.stringify({ ...formulaire, sites: [...((formulaire && formulaire.sites) || [])].sort() });

export const estModifieCompte = (formulaire, reference) => serialiser(formulaire) !== serialiser(reference);
